/**
 * Файлы элемента.
 *
 * Прямой ссылки на файл в хранилище не существует: скачивание идёт через
 * API, который сначала проверяет доступ к проекту. Поэтому ссылка ведёт на
 * собственный маршрут приложения, а не на адрес S3.
 */

import { DownloadIcon, FileIcon, Loader2Icon, PaperclipIcon, Trash2Icon } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import type { AttachmentDto } from '@project-os/contracts';
import { ATTACHMENT_LIMITS } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useDeleteAttachment, useUploadAttachment } from '@/api/assets';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/common/user-avatar';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

/** Размер файла в привычных единицах. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

const ACCEPT = ATTACHMENT_LIMITS.allowedExtensions.join(',');

export function AttachmentsSection({
  projectId,
  itemId,
  attachments,
  canEdit,
}: {
  projectId: string;
  itemId: string;
  attachments: AttachmentDto[];
  canEdit: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const upload = useUploadAttachment(projectId, itemId);
  const remove = useDeleteAttachment(projectId, itemId);

  const send = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Файлы уходят по одному: так сообщение об отказе относится к конкретному
    // файлу, а не ко всей пачке.
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync(file);
        toast.success(`Файл «${file.name}» загружен`);
      } catch (error) {
        toast.error(
          error instanceof ApiError ? error.message : `Не удалось загрузить «${file.name}»`,
        );
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDelete = async (attachment: AttachmentDto) => {
    try {
      await remove.mutateAsync(attachment.id);
      toast.success('Файл удалён');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось удалить файл');
    }
  };

  const limitMb = Math.round(ATTACHMENT_LIMITS.maxBytes / 1024 / 1024);

  return (
    <div className="space-y-3">
      {attachments.length === 0 ? (
        <p className="text-xs leading-relaxed text-muted-foreground">
          Файлов пока нет. Приложите документ, изображение или архив — не больше {limitMb} МБ.
          Исполняемые файлы загружать нельзя.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="surface-card flex items-center gap-2.5 border border-border/50 p-2.5"
            >
              <FileIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{attachment.filename}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-2xs text-muted-foreground">
                  <span>{formatBytes(attachment.sizeBytes)}</span>
                  <span>{formatDateTime(attachment.createdAt)}</span>
                  <span className="flex items-center gap-1">
                    <UserAvatar user={attachment.uploadedBy} size="xs" />
                    {attachment.uploadedBy.name}
                  </span>
                </p>
              </div>

              <Button variant="ghost" size="icon" className="size-7 shrink-0" asChild>
                <a href={attachment.downloadUrl} download={attachment.filename} title="Скачать">
                  <DownloadIcon className="size-3.5" />
                  <span className="sr-only">Скачать {attachment.filename}</span>
                </a>
              </Button>

              {attachment.canDelete && canEdit ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0 text-muted-foreground hover:text-tone-danger"
                  disabled={remove.isPending}
                  onClick={() => void handleDelete(attachment)}
                  title="Удалить"
                >
                  <Trash2Icon className="size-3.5" />
                  <span className="sr-only">Удалить {attachment.filename}</span>
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragOver(false);
            void send(event.dataTransfer.files);
          }}
          className={cn(
            'rounded-xl border border-dashed p-4 text-center transition-colors',
            dragOver ? 'border-primary bg-primary-soft' : 'border-border',
          )}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="sr-only"
            aria-label="Выбрать файлы"
            onChange={(event) => void send(event.target.files)}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={upload.isPending}
            onClick={() => inputRef.current?.click()}
          >
            {upload.isPending ? <Loader2Icon className="animate-spin" /> : <PaperclipIcon />}
            {upload.isPending ? 'Загрузка…' : 'Приложить файл'}
          </Button>
          <p className="mt-2 text-2xs leading-relaxed text-muted-foreground">
            Или перетащите файлы сюда. До {limitMb} МБ: изображения, PDF, документы Office, csv,
            json, md, zip.
          </p>
        </div>
      ) : null}
    </div>
  );
}
